"use client";

import { SectionBadge } from "./SectionBadge";
import { useI18n } from "@/i18n/context";

export function ClearGazeSection() {
  const { t } = useI18n();

  return (
    <section id="gaze" className="relative bg-bg-warm min-h-[1208px] overflow-hidden">
      {/* Badge */}
      <div className="relative z-10 pt-[76px]" data-reveal>
        <SectionBadge>{t.gaze.badge}</SectionBadge>
      </div>

      {/* Floating card: Bankable — parallax at -0.06 */}
      <div
        data-reveal
        className="absolute right-[60px] top-[239px] w-[353px] bg-white border-b-[1.5px] border-[#2f2f2f] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.25)] overflow-hidden z-20"
      >
        <div className="relative h-[68px] px-[8px] py-[16px]">
          <PillarIcon type="business" />
          <div className="ml-[32px]">
            <p className="font-clash font-bold text-[18px] text-text-primary uppercase tracking-[1.26px] leading-normal">
              {t.gaze.bankable}
            </p>
            <p className="font-jetbrains text-[10px] text-text-tertiary uppercase tracking-[0.7px] leading-normal mt-[2px]">
              {t.gaze.bankableSub}
            </p>
          </div>
          <span className="absolute right-[32px] top-[16px] font-clash font-bold text-[18px] text-text-primary uppercase tracking-[1.26px]">
            84
          </span>
          <ChevronDown className="absolute right-[7px] top-[18px]" />
        </div>
        <ProgressBar value={84} />
      </div>

      {/* Floating card: Fame — reveal only (no continuous parallax, shadow is expensive) */}
      <div
        data-reveal
        className="absolute left-[60px] top-[681px] w-[353px] bg-white border-b-[1.5px] border-text-secondary shadow-[0px_4px_12px_0px_rgba(0,0,0,0.25)] overflow-hidden z-20"
      >
        <div className="relative h-[68px] px-[8px] py-[16px]">
          <PillarIcon type="language" />
          <div className="ml-[32px]">
            <p className="font-clash font-bold text-[18px] text-text-primary uppercase tracking-[1.26px] leading-normal">
              {t.gaze.fame}
            </p>
            <p className="font-jetbrains text-[10px] text-text-tertiary uppercase tracking-[0.7px] leading-normal mt-[2px]">
              {t.gaze.fameSub}
            </p>
          </div>
          <span className="absolute right-[32px] top-[16px] font-clash font-bold text-[18px] text-text-primary uppercase tracking-[1.26px]">
            65
          </span>
          <ChevronDown className="absolute right-[7px] top-[18px]" />
        </div>
        <ProgressBar value={59} />
      </div>

      {/* Main text content — stagger reveal */}
      <div className="relative z-10 max-w-[776px] mx-auto text-center pt-[260px] px-8">
        <h2 data-reveal className="font-clash font-bold text-[38px] text-text-primary uppercase tracking-[2.66px] leading-normal mb-[48px]">
          {t.gaze.title}
        </h2>

        <div data-reveal data-reveal-stagger className="font-besley text-[24px] text-text-secondary tracking-[1.68px] leading-[28px] space-y-[28px]">
          {t.gaze.lines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="h-[10px] w-full bg-teal-mid border-t border-b border-text-secondary relative">
      <div
        className="absolute inset-y-0 left-0 bg-teal-light"
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

function ChevronDown({ className = "" }: { className?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className={className}>
      <path d="M5 7L9 11L13 7" stroke="#222F40" strokeWidth="1.5" />
    </svg>
  );
}

function PillarIcon({ type }: { type: "business" | "language" }) {
  if (type === "business") {
    return (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="absolute left-[8px] top-[17px]">
        <path d="M3 15V7.5H6V15H3ZM7.5 15V3H10.5V15H7.5ZM12 15V9.75H15V15H12Z" fill="#91969c" />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="absolute left-[8px] top-[17px]">
      <circle cx="9" cy="9" r="7" stroke="#91969c" strokeWidth="1.5" />
      <path d="M2 9H16M9 2C7 5 7 13 9 16M9 2C11 5 11 13 9 16" stroke="#91969c" strokeWidth="1.2" />
    </svg>
  );
}
